import { useEffect, useRef, useState } from 'react'
import {
  searchCatalogMaterials,
  type CatalogMaterialSearchItem,
} from '../services/catalogMaterialsService.ts'

type SearchStatus = 'idle' | 'loading' | 'ready' | 'error'

export function useDebouncedCatalogMaterialSearch(
  query: string,
  options?: {
    enabled?: boolean
    debounceMs?: number
    limit?: number
    excludeIds?: number[]
    categoryId?: string
  }
) {
  const enabled = options?.enabled !== false
  const debounceMs = options?.debounceMs ?? 300
  const trimmed = query.trim()
  const [items, setItems] = useState<CatalogMaterialSearchItem[]>([])
  const [status, setStatus] = useState<SearchStatus>('idle')
  const [error, setError] = useState('')
  const requestIdRef = useRef(0)

  useEffect(() => {
    if (!enabled) {
      setItems([])
      setStatus('idle')
      setError('')
      return
    }
    if (trimmed.length < 2) {
      setItems([])
      setStatus('idle')
      setError('')
      return
    }

    const requestId = ++requestIdRef.current
    setStatus('loading')
    setError('')

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await searchCatalogMaterials({
            q: trimmed,
            limit: options?.limit ?? 40,
            excludeIds: options?.excludeIds,
            categoryId: options?.categoryId,
          })
          if (requestIdRef.current !== requestId) return
          setItems(result)
          setStatus('ready')
        } catch {
          if (requestIdRef.current !== requestId) return
          setItems([])
          setStatus('error')
          setError('No se pudo buscar en el catálogo.')
        }
      })()
    }, debounceMs)

    return () => {
      window.clearTimeout(timer)
    }
  }, [
    trimmed,
    enabled,
    debounceMs,
    options?.limit,
    options?.categoryId,
    options?.excludeIds?.join(','),
  ])

  return { items, status, error, queryTooShort: trimmed.length > 0 && trimmed.length < 2 }
}
